import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@lib/api';
import { useNotificationStore } from '@stores/notificationStore';
import { useAuthStore } from '@stores/authStore';
import {
  formatExpiryDateInput,
  normalizeClaimStatus,
  normalizeLicenseState,
  parseExpiryDateInput,
  toLicenseUpsertBody,
} from '@lib/licenseUpdate';
import { FeatureFlagsManager } from '@components/FeatureFlagsManager';

interface ClaimCodeData {
  code: string;
  status: string;
  tier: string;
  seat_limit: number;
  token_cap_monthly: number;
  trial_days: number;
  consumed_install_id: string | null;
  consumed_at: number | null;
  issued_at: number;
  expires_at: number | null;
  revoked_at: number | null;
  revoked_reason: string | null;
  bound_sf_org_id: string;
  notes: string;
  company_name?: string;
  admin_email?: string;
  sf_org_id?: string;
}

interface InstallDetails {
  installId: string;
  appId: string;
  sfOrgId: string;
  sfOrgType: string;
  companyName: string;
  adminEmail: string;
  adminName: string;
  status: string;
  masterKeyVersion: number;
}

interface LicenseDetails {
  install_id: string;
  tier: string;
  seat_limit: number;
  token_cap_monthly: number;
  starts_at: number;
  expires_at: number;
  grace_period_days: number;
  state: string;
  kill_switch: number;
  updated_at: number;
  app_id: string;
}

const ClaimCodeDetail: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [showEditStatus, setShowEditStatus] = useState(false);
  const [editStatus, setEditStatus] = useState('issued');
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [showEditLicense, setShowEditLicense] = useState(false);
  const [editLicense, setEditLicense] = useState<Partial<LicenseDetails>>({});
  const [isSavingLicense, setIsSavingLicense] = useState(false);
  const [showFlagManager, setShowFlagManager] = useState(false);
  const { addNotification } = useNotificationStore();
  const { user } = useAuthStore();

  const CLAIM_STATUSES = ['issued', 'consumed', 'revoked', 'expired'];

  // For now, we'll fetch from the API endpoint which returns enriched data
  // In production, we'd need to store claim code details in the dashboard DB
  const { data: claimCodeData, isLoading, refetch: refetchClaimCode } = useQuery({
    queryKey: ['claim-code-detail', code],
    queryFn: async () => {
      // Fetch all claim codes and find the matching one
      const response = await apiClient.get<any>('/api/claim-codes-with-installs?app=license_reclaim');
      const claimCode = response.items?.find((c: any) => c.code === code);
      return claimCode as ClaimCodeData;
    },
    enabled: !!code,
  });

  // Fetch install details if consumed
  const { data: installData } = useQuery({
    queryKey: ['install-detail', claimCodeData?.consumed_install_id],
    queryFn: async () => {
      if (!claimCodeData?.consumed_install_id) return null;
      const response = await apiClient.get<any>(`/api/installs?id=${claimCodeData.consumed_install_id}`);
      return response?.install;
    },
    enabled: !!claimCodeData?.consumed_install_id,
  });

  // Fetch license details
  const { data: licenseData, refetch: refetchLicense } = useQuery({
    queryKey: ['license-detail', claimCodeData?.consumed_install_id],
    queryFn: async () => {
      if (!claimCodeData?.consumed_install_id) return null;
      const response = await apiClient.get<any>(`/api/installs?id=${claimCodeData.consumed_install_id}`);
      return response?.license;
    },
    enabled: !!claimCodeData?.consumed_install_id,
  });

  // Fetch feature flags for the install via dashboard API
  const { data: flagsData, refetch: refetchFlags } = useQuery({
    queryKey: ['install-flags', claimCodeData?.consumed_install_id],
    queryFn: async () => {
      if (!claimCodeData?.consumed_install_id) return null;
      try {
        const response = await apiClient.get<any>(
          `/api/install-flags?install_id=${claimCodeData.consumed_install_id}`
        );
        return response || [];
      } catch (error) {
        console.log('Error fetching flags:', error);
        return [];
      }
    },
    enabled: !!claimCodeData?.consumed_install_id,
  });

  const handleUpdateStatus = async (newStatus: string) => {
    const status = normalizeClaimStatus(newStatus);
    if (!status) {
      addNotification({ message: `Error: unknown status "${newStatus}"`, type: 'error' });
      return;
    }
    setEditStatus(status);
    setIsSavingStatus(true);
    try {
      await apiClient.put(`/api/claim-codes/${code}`, { status });
      setShowEditStatus(false);
      await refetchClaimCode();
      addNotification({ message: `Claim code status updated to ${status}`, type: 'success' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update status';
      addNotification({ message: `Error: ${errorMessage}`, type: 'error' });
      console.error('Failed to update status:', error);
    } finally {
      setIsSavingStatus(false);
    }
  };

  const handleUpdateLicense = async (e: React.FormEvent) => {
    e.preventDefault();
    const installId = claimCodeData?.consumed_install_id;
    if (!installId) {
      addNotification({ message: 'Error: no consumed install for this claim code', type: 'error' });
      return;
    }
    const body = toLicenseUpsertBody(licenseData, editLicense, user?.email);
    if (!body) {
      addNotification({
        message: 'Error: check expiry date, grace period (0-365), and license fields',
        type: 'error',
      });
      return;
    }
    setIsSavingLicense(true);
    try {
      await apiClient.put(`/api/installs/${installId}/license`, body);
      setShowEditLicense(false);
      await refetchLicense();
      addNotification({ message: 'License updated successfully', type: 'success' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update license';
      addNotification({ message: `Error: ${errorMessage}`, type: 'error' });
      console.error('Failed to update license:', error);
    } finally {
      setIsSavingLicense(false);
    }
  };


  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading claim code details...</div>
      </div>
    );
  }

  if (!claimCodeData) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Claim code not found</div>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => navigate('/claim-codes')}
        className="mb-6 text-blue-600 hover:text-blue-800 underline text-sm"
      >
        ← Back to Claim Codes
      </button>

      {/* Claim Code Details */}
      <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 font-mono mb-2">{claimCodeData.code}</h1>
          </div>
          <button
            onClick={() => {
              if (!showEditStatus) {
                setEditStatus(normalizeClaimStatus(claimCodeData.status) ?? 'issued');
              }
              setShowEditStatus(!showEditStatus);
            }}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
          >
            {showEditStatus ? 'Cancel' : 'Update Status'}
          </button>
        </div>

        {showEditStatus ? (
          <div className="space-y-3 mb-4">
            <div className="grid grid-cols-4 gap-2">
              {CLAIM_STATUSES.map((status) => (
                <button
                  key={status}
                  onClick={() => handleUpdateStatus(status)}
                  disabled={isSavingStatus}
                  className={`px-3 py-2 rounded text-sm font-medium transition disabled:opacity-50 ${
                    editStatus === status
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {isSavingStatus && editStatus === status
                    ? 'Saving…'
                    : status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <div className="text-sm text-gray-600 mb-1">Status</div>
              <span
                className={`px-3 py-1 rounded text-sm font-medium ${
                  claimCodeData.status === 'active' || claimCodeData.status === 'issued'
                    ? 'bg-green-100 text-green-800'
                    : claimCodeData.status === 'consumed'
                      ? 'bg-blue-100 text-blue-800'
                      : claimCodeData.status === 'revoked'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                }`}
              >
                {claimCodeData.status}
              </span>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-1">Tier</div>
              <div className="text-lg font-semibold text-gray-900">{claimCodeData.tier}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-1">Seats / Tokens</div>
              <div className="text-gray-700">
                {claimCodeData.seat_limit} seats, {(claimCodeData.token_cap_monthly / 1000000).toFixed(1)}M tokens/mo
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-1">Trial Days</div>
              <div className="text-gray-700">{claimCodeData.trial_days} days</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-1">Issued</div>
              <div className="text-gray-700 text-sm">{new Date(claimCodeData.issued_at * 1000).toLocaleDateString()}</div>
            </div>
            {claimCodeData.consumed_at && (
              <div>
                <div className="text-sm text-gray-600 mb-1">Consumed</div>
                <div className="text-gray-700 text-sm">{new Date(claimCodeData.consumed_at * 1000).toLocaleDateString()}</div>
              </div>
            )}
            {claimCodeData.expires_at && (
              <div>
                <div className="text-sm text-gray-600 mb-1">Expires</div>
                <div className="text-gray-700 text-sm">{new Date(claimCodeData.expires_at * 1000).toLocaleDateString()}</div>
              </div>
            )}
            {claimCodeData.revoked_at && (
              <div>
                <div className="text-sm text-gray-600 mb-1">Revoked</div>
                <div className="text-gray-700 text-sm">
                  {new Date(claimCodeData.revoked_at * 1000).toLocaleDateString()} ({claimCodeData.revoked_reason})
                </div>
              </div>
            )}
          </div>
        )}

        <div className="border-t border-gray-200 pt-4">
          <div className="text-sm text-gray-600 mb-1">Notes</div>
          <div className="text-gray-700">{claimCodeData.notes || '—'}</div>
        </div>
      </div>

      {/* Install Details */}
      {claimCodeData.consumed_install_id ? (
        <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Install Details</h2>
            <button
              onClick={() => navigate(`/customers/${claimCodeData.consumed_install_id}`)}
              className="text-blue-600 hover:text-blue-800 underline text-sm font-normal"
            >
              View Full Details →
            </button>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-sm text-gray-600 mb-1">Company</div>
              <div className="text-lg font-semibold text-gray-900">{claimCodeData.company_name || installData?.companyName || 'N/A'}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-1">Admin Email</div>
              <div className="text-gray-700">{claimCodeData.admin_email || installData?.adminEmail || 'N/A'}</div>
            </div>
            {installData?.adminName && (
              <div>
                <div className="text-sm text-gray-600 mb-1">Admin Name</div>
                <div className="text-gray-700">{installData.adminName}</div>
              </div>
            )}
            {installData?.sfOrgId && (
              <div>
                <div className="text-sm text-gray-600 mb-1">Salesforce Org ID</div>
                <div className="text-gray-700 font-mono text-sm">{installData.sfOrgId}</div>
              </div>
            )}
            {installData?.sfOrgType && (
              <div>
                <div className="text-sm text-gray-600 mb-1">Org Type</div>
                <div className="text-gray-700">{installData.sfOrgType}</div>
              </div>
            )}
            {installData?.status && (
              <div>
                <div className="text-sm text-gray-600 mb-1">Install Status</div>
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">{installData.status}</span>
              </div>
            )}
            {installData?.installId && (
              <div>
                <div className="text-sm text-gray-600 mb-1">Install ID</div>
                <div className="text-gray-700 font-mono text-sm">{installData.installId}</div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 mb-6 text-center text-gray-500">
          This claim code has not been consumed yet.
        </div>
      )}

      {/* License Details */}
      {claimCodeData.consumed_install_id && licenseData ? (
        <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-2xl font-bold text-gray-900">License Details</h2>
            <button
              onClick={() => {
                setEditLicense(licenseData);
                setShowEditLicense(!showEditLicense);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
            >
              {showEditLicense ? 'Cancel' : 'Edit License'}
            </button>
          </div>

          {showEditLicense ? (
            <form onSubmit={handleUpdateLicense} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <select
                    value={normalizeLicenseState(editLicense.state) ?? 'active'}
                    onChange={(e) => setEditLicense({ ...editLicense, state: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="expired">Expired</option>
                    <option value="terminated">Terminated</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kill Switch</label>
                  <select
                    value={editLicense.kill_switch ? '1' : '0'}
                    onChange={(e) => setEditLicense({ ...editLicense, kill_switch: e.target.value === '1' ? 1 : 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="0">Off</option>
                    <option value="1">On (Disabled)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expires At</label>
                  <input
                    type="date"
                    value={formatExpiryDateInput(editLicense.expires_at)}
                    onChange={(e) => {
                      const secs = parseExpiryDateInput(e.target.value);
                      if (secs !== null) {
                        setEditLicense({ ...editLicense, expires_at: secs });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grace Period (days)</label>
                  <input
                    type="number"
                    min={0}
                    max={365}
                    value={editLicense.grace_period_days ?? ''}
                    onChange={(e) =>
                      setEditLicense({
                        ...editLicense,
                        grace_period_days: e.target.value === '' ? undefined : parseInt(e.target.value, 10),
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={isSavingLicense}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50"
                >
                  {isSavingLicense ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditLicense(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="text-sm text-gray-600 mb-1">State</div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${licenseData.state === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {licenseData.state}
                </span>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Kill Switch</div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${licenseData.kill_switch ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                  {licenseData.kill_switch ? 'ON (Disabled)' : 'OFF'}
                </span>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Tier / Seats</div>
                <div className="text-gray-700">{licenseData.tier} - {licenseData.seat_limit} seats</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Token Cap</div>
                <div className="text-gray-700">{(licenseData.token_cap_monthly / 1000000).toFixed(1)}M/month</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Starts At</div>
                <div className="text-gray-700 text-sm">{new Date(licenseData.starts_at * 1000).toLocaleDateString()}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Expires At</div>
                <div className="text-gray-700 text-sm">{new Date(licenseData.expires_at * 1000).toLocaleDateString()}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Grace Period</div>
                <div className="text-gray-700">{licenseData.grace_period_days} days</div>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Feature Flags */}
      {claimCodeData.consumed_install_id ? (
        <FeatureFlagsManager
          installId={claimCodeData.consumed_install_id}
          flags={Array.isArray(flagsData) ? flagsData : []}
          onFlagsChange={() => refetchFlags()}
          showManage={showFlagManager}
          onManageToggle={setShowFlagManager}
          appId={installData?.appId}
        />
      ) : null}
    </div>
  );
};

export default ClaimCodeDetail;
